import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "./Header";
import SideNav from "./SideNav";
import Footer from "./Footer";
import PageHeader from "./PageHeader";
import { speakWithAvatar, toggleMute, stopAvatarSpeech, isAvatarSpeaking } from "../ttsPlayer";
import { startSTT, stopSTT } from "../sttClient";
import { getUserInitials } from "../utils/userUtils";
import { mapLanguage, mapLevel, getQuestionsLabel } from "../utils/languageUtils";
import usePageTimeTracker from '../hooks/usePageTimeTracker';
import "./Common.css";
import "./Practice.css";
import "./Learn.css";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5001/lb-api";

const WritingPage = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const messagesContainerRef = useRef(null);
  const rightContentRef = useRef(null);
  const inputRef = useRef(null);
  const promptsOverlayRef = useRef(null);
  const translationPanelRef = useRef(null);
  const conversationIdRef = useRef(null);
  const {
    language: initialLanguage = "german",
    proficiencyLevel: initialLevel = "beginner",
    avatar: initialAvatar = "Max-business.mp4",
  } = location.state || {};

  const [language, setLanguage] = useState(initialLanguage);
  const [proficiencyLevel, setProficiencyLevel] = useState(initialLevel);
  const [inputText, setInputText] = useState("");
  const [showPrompts, setShowPrompts] = useState(true);
  const [showAllPrompts, setShowAllPrompts] = useState(false);
  const [showSkillDropdown, setShowSkillDropdown] = useState(false);
  const skillDropdownRef = useRef(null);
  const [currentAvatar, setCurrentAvatar] = useState(initialAvatar);
  const [isMuted, setIsMuted] = useState(false);
  const [isReadingWelcome, setIsReadingWelcome] = useState(false);
  const languageToISOCode = (lang) => {
    const mapping = {
      'german': 'de', 'english': 'en', 'french': 'fr',
      'spanish': 'es', 'italian': 'it', 'japanese': 'ja',
      'de': 'de', 'en': 'en', 'fr': 'fr', 'es': 'es', 'it': 'it', 'ja': 'ja'
    };
    return mapping[lang.toLowerCase()] || 'de';
  };

  // Convert proficiency level to kebab-case format for PageHeader
  const levelToKebabCase = (level) => {
    const mapping = {
      'beginner': 'beginner-a1', 'elementary': 'elementary-a2',
      'pre-intermediate': 'pre-intermediate-b1', 'intermediate': 'intermediate-b2',
      'advanced': 'advanced-c1', 'proficient': 'proficient-c2',
      'beginner-a1': 'beginner-a1', 'elementary-a2': 'elementary-a2',
      'pre-intermediate-b1': 'pre-intermediate-b1', 'intermediate-b2': 'intermediate-b2',
      'advanced-c1': 'advanced-c1', 'proficient-c2': 'proficient-c2',
      // Japanese JLPT levels
      'beginner-n5': 'beginner-n5', 'elementary-n4': 'elementary-n4',
      'pre-intermediate-n3': 'pre-intermediate-n3', 'intermediate-n2': 'intermediate-n2',
      'advanced-n1': 'advanced-n1'
    };
    return mapping[level.toLowerCase()] || level;
  };
  const [prompts, setPrompts] = useState([]);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(true);
  const [showPromptsOverlay, setShowPromptsOverlay] = useState(false);
  const [hasSubmittedPrompt, setHasSubmittedPrompt] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [sttActive, setSttActive] = useState(false);
  const [finalTranscript, setFinalTranscript] = useState("");
  const [partialTranscript, setPartialTranscript] = useState("");

  // Translation state
  const [showTranslation, setShowTranslation] = useState(false);
  const [translatingText, setTranslatingText] = useState("");
  const [translatedText, setTranslatedText] = useState("");
  const [translationLabel, setTranslationLabel] = useState("English");

  // Say It Aloud state
  const [readingMessageId, setReadingMessageId] = useState(null);
  const [isReading, setIsReading] = useState(false);

  // Exercise management for continuous flow
  const [exercises, setExercises] = useState([]);
  const [currentExercise, setCurrentExercise] = useState(null);
  const [sessionId, setSessionId] = useState(null); // Track session_id for conversation continuity

  usePageTimeTracker('practice-writing', user);

  // Cleanup STT on unmount (page navigation)
  useEffect(() => {
    return () => { stopSTT(); };
  }, []);

  // Close prompts overlay when clicking outside
  useEffect(() => {
    if (!showPromptsOverlay) return;
    const handleClickOutside = (e) => {
      if (promptsOverlayRef.current && !promptsOverlayRef.current.contains(e.target)) {
        setShowPromptsOverlay(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPromptsOverlay]);

  // Close translation panel when clicking outside
  useEffect(() => {
    if (!showTranslation) return;
    const handleClickOutside = (e) => {
      if (translationPanelRef.current && !translationPanelRef.current.contains(e.target)) {
        setShowTranslation(false);
        setTranslatingText('');
        setTranslatedText('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTranslation]);

  // Close skill dropdown when clicking outside
  useEffect(() => {
    if (!showSkillDropdown) return;
    const handleClickOutside = (e) => {
      if (skillDropdownRef.current && !skillDropdownRef.current.contains(e.target)) {
        setShowSkillDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSkillDropdown]);

  // Fetch prompts from database
  useEffect(() => {
    let cancelled = false;
    const fetchPrompts = async () => {
      const mappedLanguage = mapLanguage(language);
      let mappedLevel = mapLevel(proficiencyLevel);

      // Fix level-language mismatch: use default level if JLPT with non-Japanese or vice versa
      const isJLPT = /^N[1-5]$/i.test(mappedLevel);
      const isJapanese = language === 'ja' || language === 'japanese';
      if (isJLPT && !isJapanese) mappedLevel = 'A1';
      if (!isJLPT && isJapanese && mappedLevel) mappedLevel = 'N5';

      try {
        setIsLoadingPrompts(true);
        const response = await fetch(`${API_BASE_URL}/practice/prompts`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            skill: "Writing",
            language: mappedLanguage,
            proficiencyLevel: mappedLevel,
          }),
        });

        if (cancelled) return;
        const data = await response.json();
        if (data.success && data.prompts) {
          setPrompts(data.prompts.map((p) => p.text));
        } else {
          setPrompts([
            "Write a short introduction about yourself",
            "Describe your daily routine",
            "Write about your favorite hobby",
            "Describe your hometown",
            "Write a short email to a friend",
            "Describe what you did last weekend",
          ]);
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Error fetching prompts:", error);
        setPrompts([
          "Write a short introduction about yourself",
          "Describe your daily routine",
          "Write about your favorite hobby",
          "Describe your hometown",
          "Write a short email to a friend",
          "Describe what you did last weekend",
        ]);
      } finally {
        if (!cancelled) setIsLoadingPrompts(false);
      }
    };

    fetchPrompts();
    return () => { cancelled = true; };
  }, [language, proficiencyLevel]);

  const displayedPrompts = showAllPrompts ? prompts : prompts.slice(0, 3);

  const breadcrumbs = [
    { label: "Home", path: "/", current: false },
    { label: "Explore Learning", path: "/explore-learning", current: false },
    { label: "Practice", path: "/explore-learning", current: false },
    { label: "Language Skills", current: true },
  ];

  const skills = [
    { value: "reading", label: "Reading", route: "/practice-reading" },
    { value: "writing", label: "Writing", route: "/practice-writing" },
    { value: "listening", label: "Listening", route: "/practice-listening" },
    { value: "speaking", label: "Speaking", route: "/practice-speaking" },
  ];

  const currentSkill = "writing";

  const getAvatarInitial = () => {
    const avatarLower = String(currentAvatar).toLowerCase();
    if (avatarLower.includes("max") || avatarLower.includes("soren"))
      return "S";
    return "N";
  };

  const getAvatarName = () => {
    const avatarLower = String(currentAvatar).toLowerCase();
    if (avatarLower.includes("max") || avatarLower.includes("soren"))
      return "Soren";
    return "Nebula";
  };

  const getWelcomeMessage = () => {
    const name = getAvatarName();
    const lang = (language || "german").toLowerCase();
    const langMap = {
      german: "German",
      deutsch: "German",
      de: "German",
      french: "French",
      français: "French",
      fr: "French",
      spanish: "Spanish",
      español: "Spanish",
      espanol: "Spanish",
      es: "Spanish",
      italian: "Italian",
      italiano: "Italian",
      it: "Italian",
      japanese: "Japanese",
      日本語: "Japanese",
      ja: "Japanese",
      english: "English",
      en: "English",
    };
    const langName =
      langMap[lang] || lang.charAt(0).toUpperCase() + lang.slice(1);
    return `Hi, I am ${name}, let's practice ${langName}.`;
  };

  const getTranslateText = () => {
    const lang = (language || "german").toLowerCase();
    const langMap = {
      german: "German",
      deutsch: "German",
      de: "German",
      french: "French",
      français: "French",
      fr: "French",
      spanish: "Spanish",
      español: "Spanish",
      espanol: "Spanish",
      es: "Spanish",
      italian: "Italian",
      italiano: "Italian",
      it: "Italian",
      japanese: "Japanese",
      日本語: "Japanese",
      ja: "Japanese",
      english: "English",
      en: "English",
    };
    const langName =
      langMap[lang] || lang.charAt(0).toUpperCase() + lang.slice(1);
    return `Translate in ${langName}`;
  };

  const getSelectedLanguageName = () => {
    const lang = (language || "german").toLowerCase();
    const map = { german: "German", deutsch: "German", de: "German", french: "French", français: "French", fr: "French", spanish: "Spanish", español: "Spanish", espanol: "Spanish", es: "Spanish", italian: "Italian", italiano: "Italian", it: "Italian", japanese: "Japanese", "日本語": "Japanese", ja: "Japanese", english: "English", en: "English" };
    return map[lang] || "German";
  };

  const getWritingInstruction = () => {
    const lang = (language || "german").toLowerCase();
    const instructions = {
      german: "Schreiben Sie einen Text zum folgenden Thema.",
      deutsch: "Schreiben Sie einen Text zum folgenden Thema.",
      de: "Schreiben Sie einen Text zum folgenden Thema.",
      french: "Écrivez un texte sur le sujet suivant.",
      français: "Écrivez un texte sur le sujet suivant.",
      fr: "Écrivez un texte sur le sujet suivant.",
      spanish: "Escriba un texto sobre el siguiente tema.",
      español: "Escriba un texto sobre el siguiente tema.",
      es: "Escriba un texto sobre el siguiente tema.",
      italian: "Scrivi un testo sul seguente argomento.",
      italiano: "Scrivi un testo sul seguente argomento.",
      it: "Scrivi un testo sul seguente argomento.",
      japanese: "次のトピックについて文章を書いてください。",
      日本語: "次のトピックについて文章を書いてください。",
      ja: "次のトピックについて文章を書いてください。",
      english: "Write a text on the following topic.",
      en: "Write a text on the following topic.",
    };
    return instructions[lang] || "Write a text on the following topic.";
  };

  const avatarInitial = getAvatarInitial();
  const avatarName = getAvatarName();
  const userInitials = getUserInitials(user);

  // Voice selection
  const getVoiceForAvatar = (avatar, lang) => {
    const avatarLower = String(avatar).toLowerCase();
    const isMale = avatarLower.includes("max") || avatarLower.includes("soren");
    const voiceMap = {
      de: { male: "Hans", female: "Marlene" },
      en: { male: "Matthew", female: "Joanna" },
      fr: { male: "Mathieu", female: "Celine" },
      es: { male: "Enrique", female: "Conchita" },
      it: { male: "Giorgio", female: "Carla" },
      ja: { male: "Takumi", female: "Mizuki" },
    };
    const l = (lang || "german").toLowerCase();
    const langCode =
      l === "german" || l === "deutsch" || l === "de"
        ? "de"
        : l === "english" || l === "en"
          ? "en"
          : l === "french" || l === "français" || l === "fr"
            ? "fr"
            : l === "spanish" ||
                l === "español" ||
                l === "espanol" ||
                l === "es"
              ? "es"
              : l === "italian" || l === "italiano" || l === "it"
                ? "it"
                : l === "japanese" || l === "日本語" || l === "ja"
                  ? "ja"
                  : "de";
    return voiceMap[langCode]?.[isMale ? "male" : "female"] || "Joanna";
  };

  const latestExerciseRef = useRef(null);

  // Auto-scroll to latest response
  useEffect(() => {
    if (latestExerciseRef.current) {
      setTimeout(() => {
        if (latestExerciseRef.current) {
          latestExerciseRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    }
  }, [exercises, currentExercise]);

  const handleLanguageChange = (newLanguage) => {
    // Convert ISO code back to full language name
    const isoToLanguage = {
      'de': 'german', 'en': 'english', 'fr': 'french',
      'es': 'spanish', 'it': 'italian', 'ja': 'japanese'
    };
    setLanguage(isoToLanguage[newLanguage] || newLanguage);
  };

  const handleLevelChange = (newLevel) => {
    // Just pass through the kebab-case level directly — mapLevel handles all formats
    setProficiencyLevel(newLevel);
  };

  // Map language to STT language code
  const getSTTLanguageCode = (lang) => {
    const langMap = {
      de: "de-DE",
      en: "en-US",
      fr: "fr-FR",
      es: "es-ES",
      it: "it-IT",
      ja: "ja-JP",
      german: "de-DE",
      english: "en-US",
      french: "fr-FR",
      spanish: "es-ES",
      italian: "it-IT",
      japanese: "ja-JP",
    };
    return langMap[lang?.toLowerCase()] || "de-DE";
  };

  // Handle microphone click
  const handleMicClick = async () => {
    if (sttActive) {
      // Stop recording — keep current inputText as-is
      stopSTT();
      setSttActive(false);
      setFinalTranscript("");
      setPartialTranscript("");
    } else {
      setFinalTranscript(inputText);
      setPartialTranscript("");
      setSttActive(true);
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const wsUrl = `${protocol}//${window.location.host}/stt`;
      const sttLanguage = getSTTLanguageCode(language);
      startSTT(wsUrl, { language: sttLanguage }, ({ text, isPartial }) => {
        if (isPartial) {
          setPartialTranscript(text);
        } else {
          setFinalTranscript((prev) => (prev + " " + text).trim());
          setPartialTranscript("");
        }
      });
    }
  };

  // Update inputText when transcript changes
  useEffect(() => {
    if (sttActive) {
      const combinedText = [finalTranscript, partialTranscript]
        .filter(Boolean)
        .join(" ");
      setInputText(combinedText);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.style.height = 'auto';
          inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
          inputRef.current.style.overflowY = inputRef.current.scrollHeight > 120 ? 'auto' : 'hidden';
        }
      }, 0);
    }
  }, [finalTranscript, partialTranscript, sttActive]);

  // Refs for global Enter handler
  const inputTextRef = useRef(inputText);
  inputTextRef.current = inputText;
  const sttActiveRef = useRef(sttActive);
  sttActiveRef.current = sttActive;
  const handleSendRef = useRef(null);

  // Global Enter key handler when STT is active
  useEffect(() => {
    handleSendRef.current = handleSendMessage;
  });
  useEffect(() => {
    if (!sttActive) return;
    const handleGlobalEnter = (e) => {
      if (e.key === "Enter" && !e.shiftKey && sttActiveRef.current) {
        e.preventDefault();
        e.stopPropagation();
        const textToSend = inputTextRef.current?.trim();
        if (textToSend) {
          handleSendRef.current(textToSend);
        }
      }
    };
    document.addEventListener("keydown", handleGlobalEnter, true);
    return () => document.removeEventListener("keydown", handleGlobalEnter, true);
  }, [sttActive]);

  const handlePromptClick = (prompt) => {
    setInputText(prompt);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
        inputRef.current.style.overflowY = inputRef.current.scrollHeight > 120 ? 'auto' : 'hidden';
      }
    }, 0);
  };

  const handleSendMessage = async (overrideText) => {
    // Capture current text before stopping STT
    const currentText = overrideText || inputText.trim();

    // Always stop STT if active
    if (sttActive) {
      stopSTT();
      setSttActive(false);
      setFinalTranscript("");
      setPartialTranscript("");
    }

    if (!currentText) return;

    const userInput = currentText;
    setInputText("");
    if (inputRef.current) { inputRef.current.style.height = 'auto'; inputRef.current.style.overflowY = 'hidden'; }

    // If no current exercise, this is a prompt selection
    if (!currentExercise) {
      setShowPrompts(false);
      setHasSubmittedPrompt(true);
      setShowPromptsOverlay(false);

      const newExercise = {
        id: Date.now(),
        promptText: userInput,
        topic: null,
        instructions: null,
        conversationMessages: [],
        isLoading: true,
      };

      setCurrentExercise(newExercise);

      try {
        const mappedLanguage = mapLanguage(language);
        const mappedLevel = mapLevel(proficiencyLevel);

        const response = await fetch(
          `${API_BASE_URL}/practice/writing/generate`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              language: mappedLanguage,
              level: mappedLevel,
              prompt: userInput,
            }),
          },
        );

        if (!response.ok) throw new Error("Failed to generate writing exercise");

        const data = await response.json();
        console.log("Writing API response:", data);

        // Extract session_id and response from Writing agent
        const newSessionId = data?.content?.session_id;
        const responseText = data?.content?.response || "";

        if (newSessionId) {
          setSessionId(newSessionId);
          console.log("Session ID received:", newSessionId);
        }

        setCurrentExercise((prev) => ({
          ...prev,
          topic: responseText,
          instructions: getWritingInstruction(),
          isLoading: false,
        }));

        // Silently save conversation to DB
        try {
          if (!conversationIdRef.current) {
            const convRes = await axios.post(`${API_BASE_URL}/chat/conversation`, {
              enterprise_id: user?.email || 'local-user',
              page_name: 'practice-writing',
              title: userInput.substring(0, 100),
              language: mappedLanguage,
              level: mappedLevel
            }, { withCredentials: true });
            if (convRes.data?.conversation_id) {
              conversationIdRef.current = convRes.data.conversation_id;
            }
          }
          if (conversationIdRef.current) {
            await axios.post(`${API_BASE_URL}/chat/message`, {
              conversation_id: conversationIdRef.current,
              prompt: userInput,
              user_input: userInput,
              agent_response: responseText
            }, { withCredentials: true });
          }
        } catch (saveErr) {
          console.error('Chat save error (non-blocking):', saveErr.message);
        }
      } catch (error) {
        console.error("Error fetching writing exercise:", error);
        setCurrentExercise((prev) => ({
          ...prev,
          isLoading: false,
          error: "Failed to load exercise",
          conversationMessages: [
            ...(prev?.conversationMessages || []),
            {
              type: "bot",
              text: "There's a problem processing your request. Please try again later, or let me know if I can help.",
              timestamp: new Date(),
            },
          ],
        }));
      }
    } else {
      // If current exercise exists, this is the user's writing submission

      // Add user message and a loading indicator to current exercise
      setCurrentExercise((prev) => ({
        ...prev,
        conversationMessages: [
          ...prev.conversationMessages,
          { type: "user", text: userInput, timestamp: new Date() },
          { type: "bot", text: "", isLoading: true, timestamp: new Date() },
        ],
      }));

      try {
        const mappedLanguage = mapLanguage(language);
        const mappedLevel = mapLevel(proficiencyLevel);

        // Build request body with session_id
        const requestBody = {
          language: mappedLanguage,
          level: mappedLevel,
          prompt: userInput,
        };

        // Include session_id if we have one
        if (sessionId) {
          requestBody.session_id = sessionId;
          console.log("Sending with session_id:", sessionId);
        }

        const response = await fetch(
          `${API_BASE_URL}/practice/writing/generate`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(requestBody),
          },
        );

        if (!response.ok) throw new Error("Failed to evaluate writing");

        const data = await response.json();
        const feedbackText = data?.content?.response || "Good effort! Keep practicing your writing skills.";

        // Remove loading indicator and add feedback
        setCurrentExercise((prev) => ({
          ...prev,
          conversationMessages: [
            ...prev.conversationMessages.filter((m) => !m.isLoading),
            {
              type: "bot",
              text: feedbackText,
              isFeedback: true,
              timestamp: new Date(),
            },
          ],
        }));

        // Silently save user submission + evaluation to DB
        try {
          if (conversationIdRef.current) {
            await axios.post(`${API_BASE_URL}/chat/message`, {
              conversation_id: conversationIdRef.current,
              user_input: userInput,
              agent_response: feedbackText
            }, { withCredentials: true });
          }
        } catch (saveErr) {
          console.error('Chat save error (non-blocking):', saveErr.message);
        }

        setTimeout(() => setShowPrompts(true), 1000);
      } catch (error) {
        console.error("Error evaluating writing:", error);
        // Remove loading indicator and add error message
        setCurrentExercise((prev) => ({
          ...prev,
          conversationMessages: [
            ...prev.conversationMessages.filter((m) => !m.isLoading),
            {
              type: "bot",
              text: "There's a problem processing your request. Please try again later, or let me know if I can help.",
              timestamp: new Date(),
            },
          ],
        }));
      }
    }
  };



  const handleSkillChange = (skill) => {
    setShowSkillDropdown(false);
    if (skill.value !== currentSkill) {
      navigate(skill.route, {
        state: { language, proficiencyLevel, avatar: currentAvatar },
      });
    }
  };

  const handleAvatarSoundToggle = () => {
    const newMutedState = !isMuted;
    toggleMute(newMutedState);
    setIsMuted(newMutedState);
  };

  // Read welcome message aloud with English voice (since it's English text)
  const handleReadWelcome = async () => {
    if (isReadingWelcome) {
      stopAvatarSpeech();
      setIsReadingWelcome(false);
      return;
    }
    stopAvatarSpeech();
    setIsReadingWelcome(true);
    const avatarLower = String(currentAvatar).toLowerCase();
    const isMale = avatarLower.includes('max') || avatarLower.includes('soren');
    const englishVoice = isMale ? 'Matthew' : 'Joanna';
    try {
      await speakWithAvatar(getWelcomeMessage(), null, englishVoice);
    } catch (error) {
      console.error('TTS error:', error);
    }
    setIsReadingWelcome(false);
  };

  // Check TTS completion for welcome message
  useEffect(() => {
    if (!isReadingWelcome) return;
    const check = setInterval(() => {
      if (!isAvatarSpeaking()) {
        setIsReadingWelcome(false);
        clearInterval(check);
      }
    }, 500);
    return () => clearInterval(check);
  }, [isReadingWelcome]);

  const handleNewChat = () => {
    setExercises([]);
    setCurrentExercise(null);
    setShowPrompts(true);
    setHasSubmittedPrompt(false);
    setInputText("");
    setShowPromptsOverlay(false);
    setSessionId(null); // Clear session ID for new chat
    conversationIdRef.current = null;
  };



  // Translation functions
  const handleTranslate = async (textToTranslate, overrideSource, overrideTarget) => {
    setTranslatingText(textToTranslate);
    setShowTranslation(true);
    setTranslatedText("Translating...");

    const languageMap = {
      de: "German", en: "English", fr: "French", es: "Spanish", it: "Italian", ja: "Japanese",
      german: "German", english: "English", french: "French", spanish: "Spanish", italian: "Italian", japanese: "Japanese",
    };

    const selectedLang = languageMap[language?.toLowerCase()] || "German";
    const sourceLanguage = overrideSource || selectedLang;
    const targetLanguage = overrideTarget || "English";
    setTranslationLabel(targetLanguage);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/translate`,
        {
          text: textToTranslate,
          source_language: sourceLanguage,
          target_language: targetLanguage,
          level: proficiencyLevel || "A1",
          context: "writing practice",
        },
        {
          withCredentials: true,
        }
      );

      let translatedContent = "";
      if (response.data && response.data.translation) {
        translatedContent = response.data.translation;
      } else if (response.data && response.data.translatedText) {
        translatedContent = response.data.translatedText;
      } else {
        translatedContent = "Translation not available";
      }

      // Don't store the original text - we only want to show the translation
      setTranslatedText(translatedContent);
    } catch (error) {
      console.error("Translation error:", error);
      setTranslatedText(
        "Translation service unavailable. Please try again later."
      );
    }
  };

  const handleCloseTranslation = () => {
    setShowTranslation(false);
    setTranslatingText("");
  };

  // Say It Aloud function using TTS
  // overrideLang: optional language override (e.g. 'english' for welcome message)
  const handleReadAloud = async (text, messageId, overrideLang) => {
    if (readingMessageId === messageId && isReading) {
      // If clicking same message while reading, stop TTS
      stopAvatarSpeech();
      setIsReading(false);
      setReadingMessageId(null);
      return;
    }

    // Stop any current TTS or browser speech
    stopAvatarSpeech();
    window.speechSynthesis.cancel();

    // Set reading state
    setIsReading(true);
    setReadingMessageId(messageId);

    try {
      // Use override language for English content (welcome message), otherwise use selected language
      const selectedVoice = getVoiceForAvatar(currentAvatar, overrideLang || language);

      // Call TTS service (pass null for video element since we're using static images)
      await speakWithAvatar(text, null, selectedVoice);

      // Reset reading state after a delay (TTS audio duration is unknown, so we keep it active)
      // User can click button again to stop manually
    } catch (error) {
      console.error("TTS error:", error);
      setIsReading(false);
      setReadingMessageId(null);
    }
  };

  // Listen for TTS completion to reset reading state
  useEffect(() => {
    const checkTTSStatus = setInterval(() => {
      if (isReading && !isAvatarSpeaking()) {
        setIsReading(false);
        setReadingMessageId(null);
      }
    }, 500);

    return () => clearInterval(checkTTSStatus);
  }, [isReading]);

  // Helper function to render a single exercise
  const renderExercise = (exercise, isCompleted = false) => {
    if (!exercise) return null;

    return (
      <React.Fragment key={exercise.id}>
        {/* User's Prompt */}
        <div
          className="practice-message-row user-message-row"
          style={{ justifyContent: "flex-end", marginTop: "16px" }}
          ref={!isCompleted ? latestExerciseRef : null}
        >
          <div
            className="practice-message-content-wrapper"
            style={{ maxWidth: "60%" }}
          >
            <div
              className="practice-user-message-text"
             
            >
              {exercise.promptText}
            </div>
          </div>
          <div
            className="practice-avatar-circle user-avatar"
            style={{ background: "#A100FF", marginLeft: "16px" }}
          >
            {userInitials}
          </div>
        </div>

        {/* Loading State */}
        {exercise.isLoading && (
          <div
            className="practice-message-row bot-message-row"
            style={{ marginTop: "16px", alignItems: "flex-start" }}
          >
            <div className="practice-avatar-circle bot-avatar">
              {avatarInitial}
            </div>
            <div
              className="practice-message-content-wrapper"
              style={{ flex: 1 }}
            >
              <div className="practice-typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}

        {/* Bot Response with Writing Topic */}
        {!exercise.isLoading && exercise.topic && (
          <div
            className="practice-message-row bot-message-row"
            style={{ marginTop: "16px", alignItems: "flex-start" }}
          >
            <div className="practice-avatar-circle bot-avatar">
              {avatarInitial}
            </div>
            <div
              className="practice-message-content-wrapper"
              style={{ flex: 1 }}
            >
              <div
                className="practice-bot-message-text"
                style={{ fontWeight: "600", marginBottom: "8px" }}
              >
                {exercise.instructions}
              </div>

              <div className="practice-reading-passage-box" style={{ whiteSpace: 'pre-wrap' }}>
                {exercise.topic}
              </div>

              <div
                className="practice-message-actions"
                style={{ marginTop: "12px" }}
              >
                <button
                  className="practice-action-btn"
                  onClick={() => handleTranslate(exercise.topic)}
                >
                  <img
                    src="/images/translateiconactive.svg"
                    alt="Translate"
                    width="16"
                    height="16"
                  />
                  Translate in English
                </button>
                <button
                  className="practice-action-btn"
                  onClick={() =>
                    handleReadAloud(
                      `${exercise.instructions} ${exercise.topic}`,
                      `writing-topic-${exercise.id}`
                    )
                  }
                >
                  <img
                    src={
                      isReading &&
                      readingMessageId === `writing-topic-${exercise.id}`
                        ? "/images/muteicon.svg"
                        : "/images/readicon.svg"
                    }
                    alt="Say It Aloud"
                    width="16"
                    height="16"
                  />
                  {isReading &&
                  readingMessageId === `writing-topic-${exercise.id}`
                    ? "Stop"
                    : "Say It Aloud"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error state - show error messages even without topic */}
        {!exercise.isLoading &&
          !exercise.topic &&
          exercise.conversationMessages.length > 0 && (
            <div
              className="practice-message-row bot-message-row"
              style={{ marginTop: "16px", alignItems: "flex-start" }}
            >
              <div className="practice-avatar-circle bot-avatar">
                {avatarInitial}
              </div>
              <div
                className="practice-message-content-wrapper"
                style={{ flex: 1 }}
              >
                {exercise.conversationMessages.map((msg, idx) => (
                  <div key={idx} className="practice-bot-message-text">
                    {msg.text}
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Conversation Messages */}
        {exercise.conversationMessages &&
          exercise.conversationMessages.map((message, index) => {
            if (message.type === "user") {
              return (
                <div
                  key={`${exercise.id}-msg-${index}`}
                  className="practice-message-row user-message-row"
                  style={{ justifyContent: "flex-end", marginTop: "16px" }}
                >
                  <div
                    className="practice-message-content-wrapper"
                    style={{ maxWidth: "60%" }}
                  >
                    <div
                      className="practice-user-message-text"
                    >
                      {message.text}
                    </div>
                  </div>
                  <div
                    className="practice-avatar-circle user-avatar"
                    style={{ background: "#A100FF", marginLeft: "16px" }}
                  >
                    {userInitials}
                  </div>
                </div>
              );
            } else if (message.type === "bot") {
              const isLatest =
                index === exercise.conversationMessages.length - 1;
              const isLastMsg = index === exercise.conversationMessages.length - 1;
              return (
                <div
                  key={`${exercise.id}-msg-${index}`}
                  className="practice-message-row bot-message-row"
                  style={{ marginTop: "16px", alignItems: "flex-start" }}
                  ref={isLastMsg ? latestExerciseRef : null}
                >
                  <div className="practice-avatar-circle bot-avatar">
                    {avatarInitial}
                  </div>
                  <div
                    className="practice-message-content-wrapper"
                    style={{ flex: 1 }}
                  >
                    {message.isLoading ? (
                      <div className="practice-typing">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    ) : (
                      <>
                        <div
                          className={`practice-bot-message-text ${message.isFeedback ? "writing-feedback-text" : ""}`}
                          style={{ whiteSpace: 'pre-wrap' }}
                        >
                          {message.text}
                        </div>
                        <div className="practice-message-actions" style={{marginTop: '8px'}}>
						 <button className="practice-action-btn" onClick={() => handleTranslate(message.text)}>
                            <img src="/images/translateiconactive.svg" alt=" Translate in English" width="16" height="16" />
                             Translate in English
                          </button>
                          <button className="practice-action-btn" onClick={() => handleReadAloud(message.text, `writing-conv-${exercise.id}-${index}`)}>
                            <img src={isReading && readingMessageId === `writing-conv-${exercise.id}-${index}` ? "/images/muteicon.svg" : "/images/readicon.svg"} alt="Say It Aloud" width="16" height="16" />
                            {isReading && readingMessageId === `writing-conv-${exercise.id}-${index}` ? 'Stop' : 'Say It Aloud'}
                          </button>
                         
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            }
            return null;
          })}
      </React.Fragment>
    );
  };

  return (
    <div className="practice-page">
      <Header user={user} />

      <div className="nav-header-container">
        <SideNav />
        <PageHeader
          breadcrumbs={breadcrumbs}
          language={languageToISOCode(language)}
          proficiencyLevel={levelToKebabCase(proficiencyLevel)}
          onLanguageChange={handleLanguageChange}
          onLevelChange={handleLevelChange}
          showProgress={false}
        />
      </div>

      <main className="practice-content">
        {/* Practice Title */}
        <div className="practice-title-section">
          <div className="practice-title-row">
            <h1 className="practice-title">
              Practice:
              <span className="practice-skill-dropdown" ref={skillDropdownRef}>
                <span
                  className="practice-skill-selector"
                  onClick={() => setShowSkillDropdown(!showSkillDropdown)}
                >
                  <span className="practice-skill-name">Writing</span>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M6 9L12 15L18 9"
                      stroke="#A100FF"
                      strokeWidth="2"
                    />
                  </svg>
                </span>
                {showSkillDropdown && (
                  <div className="practice-skill-dropdown-menu">
                    {skills.map((skill) => (
                      <div
                        key={skill.value}
                        className={`practice-skill-dropdown-item ${currentSkill === skill.value ? "active" : ""}`}
                        onClick={() => handleSkillChange(skill)}
                      >
                        {skill.label}
                      </div>
                    ))}
                  </div>
                )}
              </span>
            </h1>
            <button
              className={`practice-new-chat-btn ${hasSubmittedPrompt ? "active" : ""}`}
              onClick={handleNewChat}
              disabled={!hasSubmittedPrompt}
            >
              New Chat
            </button>
          </div>
          <div className="practice-title-line"></div>
        </div>

        {/* Main Content Area - Full width, no avatar sidebar */}
        <div className="practice-main-area writing-no-avatar">
          {/* Full-width Content */}
          <div
            className="practice-right-content"
            ref={rightContentRef}
            style={{ maxWidth: "100%" }}
          >
            {/* Welcome Section */}
            {!hasSubmittedPrompt && (
              <div className="practice-agent-section">
                <div className="practice-agent-avatar">
                  <span>{avatarInitial}</span>
                </div>
                <div className="practice-agent-info">
                  <p className="practice-welcome-message">
                    {getWelcomeMessage()}
                  </p>
                  <div className="practice-actions">
                    <button className="practice-translate-chip" onClick={() => handleTranslate(getWelcomeMessage(), 'English', getSelectedLanguageName())}>
                      <img
                        src="/images/translateiconactive.svg"
                        alt="Translate"
                        width="18"
                        height="18"
                      />
                      {getTranslateText()}
                    </button>
					  <button
                      className="practice-action-btn"
                      onClick={() => handleReadAloud(getWelcomeMessage(), 'welcome-message', 'english')}
                    >
                      <img
                        src={
                          readingMessageId === 'welcome-message' && isReading
                            ? "/images/muteicon.svg"
                            : "/images/readicon.svg"
                        }
                        alt={readingMessageId === 'welcome-message' && isReading ? "Stop" : "Say It Aloud"}
                        width="18"
                        height="15"
                      />
                      {readingMessageId === 'welcome-message' && isReading ? 'Stop' : 'Say It Aloud'}
                    </button>
                  </div>

                

                  {showPrompts && (
                    <div className="practice-prompts-section">
                      <div className="practice-prompts-header">
                        <img
                          src="/images/Group.svg"
                          alt="Prompt icon"
                          width="12"
                          height="15"
                        />
                        <span>Explore Prompts</span>
                      </div>
                      <div className="practice-prompt-chips">
                        {isLoadingPrompts ? (
                          <div style={{ padding: "8px", color: "#707070" }}>
                            Loading prompts...
                          </div>
                        ) : displayedPrompts.length > 0 ? (
                          displayedPrompts.map((prompt, idx) => (
                            <button
                              key={idx}
                              className="practice-prompt-chip"
                              onClick={() => handlePromptClick(prompt)}
                            >
                              {prompt}
                            </button>
                          ))
                        ) : (
                          <div style={{ padding: "8px", color: "#707070" }}>
                            No prompts available
                          </div>
                        )}
                      </div>
                      {prompts.length > 3 && (
                        <button
                          className="practice-view-more-link"
                          onClick={() => setShowAllPrompts(!showAllPrompts)}
                        >
                          <img
                            src="/images/downarrowicon.png"
                            alt=""
                            width="16"
                            height="16"
                            style={{
                              transform: showAllPrompts
                                ? "rotate(180deg)"
                                : "none",
                            }}
                          />
                          {showAllPrompts ? "View less" : "View more"}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="practice-messages" ref={messagesContainerRef}>
              {exercises.map((exercise) => renderExercise(exercise, true))}
              {currentExercise && renderExercise(currentExercise, false)}
            </div>

            {/* Input Section - Fixed prompt bar like Learn page */}
          </div>
        </div>

        <div className="prompt-bar-container writing-prompt-bar">
          <div className="prompt-bar">
            <textarea
              ref={inputRef}
              className="prompt-input"
              placeholder={`Chat with ${avatarName}`}
              value={inputText}
              rows={1}
              disabled={currentExercise?.isLoading || currentExercise?.conversationMessages?.some(m => m.isLoading)}
              onChange={(e) => { setInputText(e.target.value); setShowPromptsOverlay(false); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; e.target.style.overflowY = e.target.scrollHeight > 120 ? 'auto' : 'hidden'; }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && inputText.trim() && !(currentExercise?.isLoading || currentExercise?.conversationMessages?.some(m => m.isLoading))) {
                  e.preventDefault();
                  handleSendMessage();
                  e.target.style.height = 'auto';
                }
              }}
            />
            <div className="prompt-actions">
              {hasSubmittedPrompt && (
                <button
                  className="prompt-icon-btn"
                  onClick={() => { if (!(currentExercise?.isLoading || currentExercise?.conversationMessages?.some(m => m.isLoading))) setShowPromptsOverlay(!showPromptsOverlay); }}
                  aria-label="Show prompts"
                  style={(currentExercise?.isLoading || currentExercise?.conversationMessages?.some(m => m.isLoading)) ? {opacity: 0.4, pointerEvents: 'none'} : {}}
                >
                  <img
                    src="/images/Group.svg"
                    alt="Prompts"
                    width="12"
                    height="15"
                  />
                  <span style={{ fontSize: "14px", marginLeft: "4px" }}>
                    Prompts
                  </span>
                </button>
              )}
              <button
                className={`voice-button ${sttActive ? "recording" : ""}`}
                aria-label="Voice input"
                onClick={handleMicClick}
                disabled={(currentExercise?.isLoading || currentExercise?.conversationMessages?.some(m => m.isLoading)) && !sttActive}
                style={(currentExercise?.isLoading || currentExercise?.conversationMessages?.some(m => m.isLoading)) && !sttActive ? {opacity: 0.4} : {}}
              >
                <img
                  src="/images/micicon.png"
                  alt="Mic"
                  width="24"
                  height="24"
                />
              </button>
              <button
                className={`send-button ${inputText.trim() ? "active" : ""}`}
                aria-label="Send message"
                onClick={() => handleSendMessage()}
                disabled={!inputText.trim() || currentExercise?.isLoading}
                style={{opacity: currentExercise?.isLoading ? 0.4 : 1}}
              >
                {inputText.trim() ? (
                  <img
                    src="/images/activeSend.svg"
                    alt="Send"
                    width="24"
                    height="24"
                  />
                ) : (
                  <img
                    src="/images/sendicon.png"
                    alt="Send"
                    width="24"
                    height="24"
                  />
                )}
              </button>
            </div>
          </div>

          {/* Prompts Overlay */}
          {showPromptsOverlay && hasSubmittedPrompt && (
            <div className="prompts-overlay" ref={promptsOverlayRef}>
              <div className="prompts-overlay-header">
                <img
                  src="/images/Group.svg"
                  alt="Prompts"
                  width="12"
                  height="15"
                />
                <span>Explore prompt library</span>
              </div>
              <div className="prompts-overlay-content">
                {prompts.map((prompt, idx) => (
                  <button
                    key={idx}
                    className="prompts-overlay-chip"
                    onClick={() => {
                      handlePromptClick(prompt);
                      setShowPromptsOverlay(false);
                    }}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="disclaimer">
            <p>
              Response generated by AI. Verify accuracy and privacy restrictions
              before use.{" "}
            </p>
            <div className="discalimerlinks">
              <button className="disclaimer-link" onClick={() => setShowTermsModal(true)}>Terms of use</button>{" "}
              <button className="disclaimer-link" onClick={() => setShowPrivacyModal(true)}>Privacy policy</button>
            </div>
          </div>
        </div>
      </main>

      {/* Translation Panel */}
      {showTranslation && (
        <div className="translation-panel" ref={translationPanelRef}>
          <div className="translation-header">
            <h3>Translation</h3>
            <button className="close-button" onClick={handleCloseTranslation}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="#000000"/>
              </svg>
            </button>
          </div>
          
          <div className="translation-content">
            <div className="translation-section">
              <h4>{translationLabel} Translation</h4>
              <p style={{ whiteSpace: "pre-wrap" }}>{translatedText}</p>
            </div>
          </div>
        </div>
      )}

      <Footer />

      {showPrivacyModal && (
        <div className="modal-overlay" onClick={() => setShowPrivacyModal(false)}>
          <div className="footer-privacy-content">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Privacy Policy</h2>
                <button className="modal-close" onClick={() => setShowPrivacyModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <h3>Data Privacy Statement</h3>
                <p>Please be aware that any personal data that you and others provide through this tool may be processed by Accenture.</p>
                <p>The protection of your personal data is very important to Accenture. Accenture is committed to keeping your personal data secure, and processing it in accordance with applicable data protection laws and our internal policies including our <a href="https://www.accenture.com/us-en/about/privacy-policy" target="_blank" rel="noopener noreferrer">Global Data Privacy Policy</a>.</p>
                <p>Before providing any personal data through this tool, Accenture invites you to read its <a href="https://www.accenture.com/us-en/about/privacy-policy" target="_blank" rel="noopener noreferrer">privacy statement</a>, which includes important information on why and how Accenture is processing your personal data.</p>
              </div>
              <div className="modal-footer">
                <button className="modal-agree-button" onClick={() => setShowPrivacyModal(false)}>I Agree</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTermsModal && (
        <div className="modal-overlay" onClick={() => setShowTermsModal(false)}>
          <div className="footer-term-content">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Terms of Use</h2>
                <button className="modal-close" onClick={() => setShowTermsModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <h3>Acceptance of Terms</h3>
                <p>By accessing and using Language Buddy, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use.</p>
                <h3>License and Access</h3>
                <p>Language Buddy grants you a limited, non-exclusive, non-transferable license to access and use the platform for personal language learning purposes.</p>
                <h3>User Conduct</h3>
                <p>You agree to use Language Buddy in accordance with all applicable laws and regulations.</p>
                <h3>Intellectual Property</h3>
                <p>All content, materials, features, and functionality available on Language Buddy are the property of Accenture and are protected by copyright, trademark, and other intellectual property laws.</p>
                <h3>Disclaimer of Warranties</h3>
                <p>Language Buddy is provided "as is" without warranties of any kind, either express or implied.</p>
              </div>
              <div className="modal-footer">
                <button className="modal-agree-button" onClick={() => setShowTermsModal(false)}>I Agree</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WritingPage;
